import { useEffect, useRef, useState } from "react";

/**
 * A slab's cert number, covered on its photo (D, 9 October 2026: "if next time we want to
 * sell slabs, we don't want to display the cert number"). The photo is kept as taken, and
 * where the number sits is kept with the slab; the grey box is drawn on whenever the photo
 * is shown, so it can come off again.
 */

/** Edges as fractions of the photo: 0 is the left or top, 1 the right or bottom. */
export interface CertBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** The room left round the slab when the photo is cut to it: next to none (D, 10 October 2026). */
export const CROP_MARGIN = 0.005;

/**
 * Where the cert number is on the cropped photo, given where it was on the whole photo
 * and where the slab was. No slab box, the photo was not cropped and the box stands.
 */
export function boxInCrop(slabBox: CertBox | null | undefined, certBox: CertBox | null | undefined): CertBox | null {
  if (!certBox) return null;
  if (!slabBox) return certBox;
  const x0 = Math.max(0, slabBox.left - CROP_MARGIN);
  const y0 = Math.max(0, slabBox.top - CROP_MARGIN);
  const x1 = Math.min(1, slabBox.right + CROP_MARGIN);
  const y1 = Math.min(1, slabBox.bottom + CROP_MARGIN);
  const across = (x: number): number => Math.min(1, Math.max(0, (x - x0) / (x1 - x0)));
  const down = (y: number): number => Math.min(1, Math.max(0, (y - y0) / (y1 - y0)));
  const box = { left: across(certBox.left), top: down(certBox.top), right: across(certBox.right), bottom: down(certBox.bottom) };
  return box.right > box.left && box.bottom > box.top ? box : null;
}

/** Where a box on a photo lands once the photo is cut to another box, both as fractions. */
export function boxWithin(cut: CertBox, box: CertBox | null): CertBox | null {
  if (!box) return null;
  const across = (x: number): number => Math.min(1, Math.max(0, (x - cut.left) / (cut.right - cut.left)));
  const down = (y: number): number => Math.min(1, Math.max(0, (y - cut.top) / (cut.bottom - cut.top)));
  const moved = { left: across(box.left), top: down(box.top), right: across(box.right), bottom: down(box.bottom) };
  return moved.right > moved.left && moved.bottom > moved.top ? moved : null;
}

/** A photo, by its address, cut to a box: what is kept, ready to send. */
export async function cutPhoto(src: string, cut: CertBox): Promise<{ contentType: string; data: string }> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("The photo could not be opened"));
    image.src = src;
  });
  const x = cut.left * image.naturalWidth;
  const y = cut.top * image.naturalHeight;
  const width = (cut.right - cut.left) * image.naturalWidth;
  const height = (cut.bottom - cut.top) * image.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("The photo could not be cut");
  context.drawImage(image, x, y, width, height, 0, 0, canvas.width, canvas.height);
  const data = canvas.toDataURL("image/jpeg", 0.88).split(",")[1] ?? "";
  if (!data) throw new Error("The photo could not be cut");
  return { contentType: "image/jpeg", data };
}

/** The cover a little larger than the digits, so nothing peeps out at the edges. */
function padded(box: CertBox): CertBox {
  const x = (box.right - box.left) * 0.08 + 0.005;
  const y = (box.bottom - box.top) * 0.25 + 0.005;
  return {
    left: Math.max(0, box.left - x),
    top: Math.max(0, box.top - y),
    right: Math.min(1, box.right + x),
    bottom: Math.min(1, box.bottom + y),
  };
}

const COVER = "#9ca3af";

/**
 * The photo with its cert number covered, as an address an img or a link can use. Without
 * a box, the photo's own address. Drawn in the browser, so what is opened or saved from it
 * is covered too.
 */
export function useCoveredPhoto(src: string, box: CertBox | null | undefined): string | undefined {
  const [covered, setCovered] = useState<string | undefined>(box ? undefined : src);
  const key = box ? `${box.left},${box.top},${box.right},${box.bottom}` : "";
  useEffect(() => {
    if (!box) {
      setCovered(src);
      return;
    }
    let made: string | undefined;
    let gone = false;
    setCovered(undefined);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.drawImage(image, 0, 0);
      const cover = padded(box);
      context.fillStyle = COVER;
      context.fillRect(
        cover.left * canvas.width,
        cover.top * canvas.height,
        (cover.right - cover.left) * canvas.width,
        (cover.bottom - cover.top) * canvas.height,
      );
      canvas.toBlob(
        (blob) => {
          if (!blob || gone) return;
          made = URL.createObjectURL(blob);
          setCovered(made);
        },
        "image/jpeg",
        0.9,
      );
    };
    image.src = src;
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [src, key]);
  return covered;
}

/**
 * The slab's photo on its form: the cover shown where it will go, and a way to mark the
 * number by dragging across it, for a photo the reading did not find it on.
 */
export function CertMarker({
  src,
  box,
  hidden,
  onBox,
  cutting = false,
}: {
  readonly src: string;
  readonly box: CertBox | null;
  readonly hidden: boolean;
  readonly onBox: (box: CertBox) => void;
  /** Dragging marks where to cut the photo instead of where the number is. */
  readonly cutting?: boolean;
}): React.ReactElement {
  const frame = useRef<HTMLDivElement>(null);
  const [from, setFrom] = useState<{ x: number; y: number } | undefined>();
  const [drawing, setDrawing] = useState<CertBox | undefined>();

  const at = (event: React.PointerEvent): { x: number; y: number } => {
    const rect = frame.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };
  const between = (a: { x: number; y: number }, b: { x: number; y: number }): CertBox => ({
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    right: Math.max(a.x, b.x),
    bottom: Math.max(a.y, b.y),
  });
  const shown = drawing ?? (cutting ? null : box);
  const cover = shown ? (drawing ? shown : padded(shown)) : null;

  return (
    <div
      ref={frame}
      className="relative inline-block max-w-full select-none cursor-crosshair touch-none"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = at(event);
        setFrom(point);
        setDrawing(between(point, point));
      }}
      onPointerMove={(event) => {
        if (from) setDrawing(between(from, at(event)));
      }}
      onPointerUp={(event) => {
        if (!from) return;
        const marked = between(from, at(event));
        setFrom(undefined);
        setDrawing(undefined);
        // A click, not a drag, leaves the mark as it was.
        if (marked.right - marked.left > 0.01 && marked.bottom - marked.top > 0.005) onBox(marked);
      }}
    >
      <img src={src} alt="The slab" draggable={false} className="block max-w-full max-h-[22rem] w-auto h-auto rounded-sm border border-defaultborder" />
      {cover ? (
        <div
          className={`absolute pointer-events-none ${drawing ? (cutting ? "border-2 border-success bg-success/10" : "border-2 border-primary bg-primary/20") : hidden ? "" : "border-2 border-dashed border-danger"}`}
          style={{
            left: `${cover.left * 100}%`,
            top: `${cover.top * 100}%`,
            width: `${(cover.right - cover.left) * 100}%`,
            height: `${(cover.bottom - cover.top) * 100}%`,
            ...(hidden && !drawing ? { background: COVER } : {}),
          }}
        />
      ) : null}
    </div>
  );
}
